// Story 22.1 (FR88, AC22) — which organizations the attach picker offers, and
// which it shows refused.
//
// THREE DISPOSITIONS, NOT TWO. A personal organization is never a seat host, so
// it is not a candidate at all and is not listed. An organization that IS a
// candidate but cannot take this clinician today — no headroom, or a contract
// not in force — is SHOWN AND DISABLED WITH THE REASON. Hiding the second kind
// collapses it into the first, and the administrator cannot tell "wrong search"
// from "this one is full": they retype the name they already had.
//
// The picker is a convenience and never the gate. Every refusal drawn here has a
// server code behind it (`ORG-023` no seats, `ORG-024` contract not in force,
// `ORG-025` personal), and the server re-decides on submit against state this
// list may be seconds behind.

import type { AdminOrgRow } from "@/hooks/queries/admin-orgs-types";
import type { ComboboxOption } from "@/components/ui/searchable-combobox";
import { formatDate } from "@/lib/format-date";

/**
 * Is the contract in force on `now`?
 *
 * FAILS CLOSED on a missing or unparseable date, matching the server's own
 * `ORG-024` `contract_dates_missing` leg: an organization whose window we cannot
 * read is not an organization we may assert is open. The comparison is on whole
 * DAYS in the viewer's zone, deliberately — this decides what to offer, not what
 * to enforce, and the enforcement is `lapse_instant` / `start_instant` on the
 * server, which this list must never try to reproduce to the second.
 */
export function orgContractInForce(org: AdminOrgRow, now: Date): boolean {
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const start = dayValue(org.contract_start_date);
  const end = dayValue(org.contract_end_date);
  if (start == null || end == null) return false;
  return start <= today && today <= end;
}

function dayValue(iso: string | null | undefined): number | null {
  if (!iso) return null;
  // Date-only ISO strings parse as UTC midnight, which is what `today` above is
  // built to compare against.
  const parsed = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  const value = parsed.getTime();
  return Number.isNaN(value) ? null : value;
}

/**
 * Why this candidate cannot take the clinician today, or `null` when it can.
 *
 * Both refused labels count the SAME thing the selectable one does — seats
 * AVAILABLE out of capacity — because all three sit in one list and the reader
 * scans down it. Counting consumed-of-capacity on the full row and
 * remaining-of-capacity on the selectable rows flips the first number's referent
 * mid-list, so "60 of 60" and "22 of 140" would mean opposite things two lines
 * apart. Full reads `0 of 60`, which is what Screen 39 draws.
 *
 * Dates go through `formatDate` like every other date in this dialog, and the
 * label says WHICH end of the window failed rather than printing the range: an
 * administrator refused by a contract wants to know whether it has ended or has
 * not started, and a range makes them work it out.
 */
export function attachRefusalReason(org: AdminOrgRow, now: Date): string | null {
  if (!orgContractInForce(org, now)) {
    if (!org.contract_start_date || !org.contract_end_date) {
      return "contract not in force (contract dates not recorded)";
    }
    const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
    const end = dayValue(org.contract_end_date);
    return end != null && end < today
      ? `contract not in force (ended ${formatDate(org.contract_end_date)})`
      : `contract not in force (starts ${formatDate(org.contract_start_date)})`;
  }
  if (org.remaining <= 0) {
    return `no seats available (${org.remaining} of ${org.seat_capacity}). Raise seat capacity first.`;
  }
  return null;
}

/**
 * The picker's options, in the order the server returned them.
 *
 * A candidate keeps its position whether or not it is selectable: sorting the
 * refused ones to the bottom would reintroduce the hiding this function exists
 * to prevent, one scroll further down.
 */
export function attachOrgOptions(orgs: AdminOrgRow[], now: Date): ComboboxOption[] {
  return orgs
    .filter((org) => org.org_type === "professional")
    .map((org) => {
      const reason = attachRefusalReason(org, now);
      return {
        value: org.id,
        label: reason
          ? `${org.name} — ${reason}`
          : `${org.name} — ${org.remaining} of ${org.seat_capacity} seats available`,
        disabled: reason != null,
      };
    });
}

/** The four Seat-impact figures AFTER the attach (AC23, Screen 24's grammar). */
export interface SeatImpact {
  seatCapacity: number;
  assigned: number;
  activeSeats: number | null;
  available: number;
}

/**
 * `Assigned` and `Active seats` both move, and neither is folded into the other
 * (`ux-design-specification.md` §13.13). They look redundant on an attach
 * precisely because an attach moves both; a release moves only one, which is
 * what the two columns are for.
 *
 * `activeSeats` stays `null` when the server did not report `active_seat_count`
 * — it is optional on `AdminOrgRow` — rather than being invented from
 * `seat_consumed`, which is the term's consumption and not the headcount.
 */
export function seatImpactAfterAttach(org: AdminOrgRow): SeatImpact {
  return {
    seatCapacity: org.seat_capacity,
    assigned: org.seat_consumed + 1,
    activeSeats: org.active_seat_count == null ? null : org.active_seat_count + 1,
    available: org.remaining - 1,
  };
}
